import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import toast from "react-hot-toast"
import { Link } from "react-router-dom"
import { CountdownCircleTimer } from 'react-countdown-circle-timer'

import { useUpdateCard, useCardDetails, useCardSecrets, useUpdatePinRequest, useUpdatePinConfirm } from "../../hooks/useCards"
import { CARD_ACTIONS, CardActionType, generateSessionAndSecret, decryptPayload, encryptPin } from "../../utils/cards"
import { CardInfo, CardVendors } from "../../utils/cards"
import InputAdvanced from "../../components/Input/InputAdvanced"
import PasswordEyeInput from "../../components/PasswordEyeInput/PasswordEyeInput"
import Spinner from "../../components/Spinner/Spinner"
import ClickToCopy from "../../components/ClickToCopy/ClickToCopy"
import useModalStore from "../../stores/ModalStore"
import localConfig from "../../local_config"
import "./CardActionModal.css"

interface CardActionProps {
    card: CardInfo
    action: CardActionType
}

const CardActionModal = ({ card, action } : CardActionProps) => {

    const { t } = useTranslation('common')
    const { mutateAsync: updateCard } = useUpdateCard()

    const [ isLoading, setIsLoading ] = useState(false)
    const [ sessionId, setSessionId ] = useState<string>("")
    const [ secretKey, setSecretKey ] = useState<string>("")
    const [ decryptedData, setDecryptedData ] = useState<any>({})

    const [ pin, setPin ] = useState("")
    const [ confirmPin, setConfirmPin ] = useState("")
    const [ confirmationCode, setConfirmationCode ] = useState("")
    const [ pinError, setPinError ] = useState("")
    const [ timerKey, setTimerKey ] = useState(0)
    const [ resend, setResend ] = useState(true)
    const [ pinLoading, setPinLoading ] = useState(false)
    const [ isPinReady, setIsPinReady ] = useState(false)
    const [ updatePinFormData, setUpdatePinFormData ] = useState<any>({
        confirmation_code: "",
        encrypted_data: ""
    })

    // Conditions for rendering
    const isGetCardSecrets = action === "details" && card.vendor === CardVendors.RAIN
    const isUpdateCardPin = action === "pin" && card.vendor === CardVendors.RAIN

    const isPinEqual = pin && confirmPin && pin === confirmPin
    const isPinRequestValid = isPinEqual && confirmationCode

    const { data: cardSecrets, isFetching: isCardSecretsLoading } = useCardSecrets(
        sessionId,
        isGetCardSecrets && sessionId !== ""
    )

    const { data: cardDetails, isFetching: isCardDetailsLoading } = useCardDetails(
        card.tracking_number,
        action === "details"
    )
    const cardDetailsData = card.vendor === CardVendors.LIGO ? card?.data : cardDetails

    const { mutateAsync: updatePinRequest } = useUpdatePinRequest()
    const { mutateAsync: updatePinConfirm } = useUpdatePinConfirm(sessionId, updatePinFormData)


    const { closeModal } = useModalStore((state) => {
        return {
            closeModal: state.closeModal,
        }
    })

    const handleAction = async () => {
        const new_status = CARD_ACTIONS[action]
        if (new_status) {
            try {
                setIsLoading(true)
                await updateCard({ id: card.tracking_number, formData: {"status": new_status} })
                setIsLoading(false)
                toast.success(t(`card_action_modal.${action}.success`))
                closeModal()
            }
            catch(error: any) {
                setIsLoading(false)
                if (error.response.status == 400)
                    toast.error(t(`card_action_modal.${action}.${card.vendor}_error`))
                else
                    toast.error(t('try_later_or_contact_support'))
            }
        }
    }

    const fetchPinRequest = async () => {
        setTimerKey(prevtimerKey => prevtimerKey + 1)
        setResend(false)
        await updatePinRequest()
    }

    const fetchPinConfirm = async () => {
        setPinLoading(true)
        try {
            const encryptedPin = await encryptPin(pin, secretKey)
            setUpdatePinFormData({
                confirmation_code: confirmationCode,
                encrypted_data: { encryptedPin: encryptedPin }
            })
            const result = await updatePinConfirm()
            if (result.status === 200) {
                toast.success(t("card_action_modal.pin.success"))
                closeModal()
            } else {
                toast.error(t("card_action_modal.pin.error"))
            }
        } catch (error: any) {
            if (error?.response?.data?.type == "pin_confirmation_code_invalid") {
                toast.error(t("card_action_modal.pin.error"))
            } else {
                toast.error(t("card_action_modal.pin.error"))
            }
            console.log(error)
        } finally {
            setPinLoading(false)
        }
    }

    // Generate session and secret key for rain sensible data
    useEffect(() => {
        const fetchKeys = async () => {
            try {
                if (isGetCardSecrets || isUpdateCardPin) {
                    const { sessionId: newSessionId, secretKey: newSecret } = await generateSessionAndSecret()
                    setSessionId(newSessionId)
                    setSecretKey(newSecret)
                }
            } catch (error) {
                console.error("Failed to generate session keys:", error)
            }
        }
    
        fetchKeys()
    }, [])

    // Decrypt card data
    useEffect(() => {
        const decryptKeys = async () => {
            try {
                if (cardSecrets && secretKey !== "") {
                    const decryptedPan = await decryptPayload(cardSecrets.encryptedPan, secretKey)
                    const decryptedCvc = await decryptPayload(cardSecrets.encryptedCvc, secretKey)
                    console.log(decryptedPan)
                    console.log(decryptedCvc)

                    if (decryptedPan && decryptedCvc) {
                        setDecryptedData({ 
                            pan: decryptedPan,
                            cvc: decryptedCvc,
                            expiry_mm_yyyy: card?.data?.expiry_mm_yyyy || undefined,
                            brand: card?.data?.brand || undefined,
                            type: card?.data?.type || undefined,
                        })
                        setSessionId("")
                        setSecretKey("")
                    } else {
                        setDecryptedData({})
                    }
                }
            } catch (error) {
                console.error("Failed to decrypt encrypted data:", error)
            }
        }

        decryptKeys()
    }, [cardSecrets])

    // Pin mismatch
    useEffect(() => {
        if (confirmPin && pin !== confirmPin) {
            setPinError(t("card_action_modal.pin.form.pin_error"))
        } else {
            setPinError("")
        }
    }, [confirmPin])

    return (
        <div className="card-action-status-cont">
           <h3 className="card-action-status-title">{t(`card_action_modal.${action}.title`)}</h3>

           <div className="card-action-status-info">
                {t(`card_action_modal.${action}.text`) && <div className="card-action-status-info-text">{t(`card_action_modal.${action}.text`)}</div>}

                {action === "fund" && 
                    <Link to={`/withdraw/${card?.currency}`} style={{textDecoration: "none"}} className="card-action-withdrawal-button">{t('withdraw')}</Link>
                }

                {/* Card data */}
                {isGetCardSecrets ? (
                    <div className="card-data">
                        {isCardSecretsLoading ? (
                            <Spinner />
                        ) : decryptedData ? (
                            <>
                                {Object.entries(decryptedData).map(([key, value], i) => (
                                    value != null &&
                                    <div key={i} className="card-data-item">
                                        <div className="card-data-item-title">{t(key)}</div>
                                        {key === "pan" || key === "cvc" || key === "expiry_mm_yyyy" ? (
                                            <ClickToCopy value={typeof value === "string" ? t(value) : ""} />
                                        ) : (
                                            <div className="card-data-item-value">{typeof value === "string" && t(value)}</div>
                                        )}
                                    </div>
                                ))}
                                {localConfig.countryCode != "PE" && <div className="card-data-warning">{t("nemo_card_details_warning")}</div>}
                            </>
                        ) : (
                            <div className="card-error-box">{t('please_try_again')}</div>
                        )
                        }
                    </div>
                ) : isUpdateCardPin ? (
                    <div className="card-data">
                        {isPinReady ? (
                            <>
                                <InputAdvanced
                                    field={{
                                        name: t("card_action_modal.pin.form.confirmation_code"),
                                        description: t("card_action_modal.pin.form.enter_confirmation_code"),
                                        type: "number",
                                    }}
                                    onInputChange={(_: object, value: string) => {
                                        setConfirmationCode(value)
                                        return value
                                    }}
                                />
                                <div className="card-timer-div">
                                    {resend ? (
                                        <div className="resend-code-button" onClick={fetchPinRequest}>
                                            {t("card_action_modal.pin.form.send_code")}
                                        </div>
                                    ) : (
                                        <CountdownCircleTimer
                                                key={timerKey}
                                                isPlaying
                                                size={70}
                                                duration={30}
                                                // NOTE this are overwritten in css
                                                colors="#fff"
                                                trailColor='#fff'
                                                onComplete={() => setResend(true)}
                                        >
                                            {({ remainingTime }) => remainingTime}
                                        </CountdownCircleTimer>
                                    )}
                                </div>
                                {pinLoading ? (
                                    <Spinner />
                                ) : (
                                    <div className="card-action-buttons">
                                        <button className="card-request-submit-button" onClick={() => {
                                            setIsPinReady(false)
                                        }}>
                                            {t("back")}
                                        </button>
                                        <button className="card-request-submit-button" disabled={!isPinRequestValid} onClick={fetchPinConfirm}>
                                            {t("confirm")}
                                        </button>
                                    </div>
                                )} 
                            </>
                        ) : (
                            <>
                                <div className="pin-input-box">
                                    <label htmlFor="pin">{t('card_action_modal.pin.form.enter_pin')}</label>           
                                    <PasswordEyeInput 
                                        i_value={pin} 
                                        i_placeholder="card_action_modal.pin.form.enter_pin_placeholder" 
                                        i_name="card_action_modal.pin.form.enter_pin" 
                                        i_id="pin" 
                                        handleChangeForm={(e) => {
                                            if (e?.target?.value?.length <= 4) {
                                                setPin(e.target.value)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="pin-input-box">
                                    <label htmlFor="confirm_pin">{t('card_action_modal.pin.form.confirm_pin')}</label>   
                                    <PasswordEyeInput 
                                        i_value={confirmPin} 
                                        i_placeholder="card_action_modal.pin.form.confirm_pin_placeholder" 
                                        i_name="card_action_modal.pin.form.confirm_pin" 
                                        i_id="confirm_pin" 
                                        handleChangeForm={(e) => {
                                            if (e?.target?.value?.length <= 4) {
                                                setConfirmPin(e.target.value)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="card-action-error">
                                    {pinError}
                                </div>
                                <div className="card-action-buttons">
                                    <button className="card-request-submit-button" onClick={closeModal}>
                                        {t("cancel")}
                                    </button>
                                    <button className="card-request-submit-button" 
                                        disabled={!isPinEqual} 
                                        onClick={() => {
                                            setIsPinReady(true)
                                            if (resend) fetchPinRequest()
                                        }}
                                    >
                                        {t("continue")}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : action === "details" ? (
                    <div className="card-data">
                        {isCardDetailsLoading ? <Spinner /> 
                        : 
                            <>
                            {Object.entries(cardDetailsData).map(([key, value]) => (
                                value != null &&
                                <div className="card-data-item">
                                    <div className="card-data-item-title">{t(key)}</div>
                                    <div className="card-data-item-value">{typeof value === "string" && t(value)}</div>
                                </div>
                            ))}
                            {localConfig.countryCode != "PE" && <div className="card-data-warning">{t("nemo_card_details_warning")}</div>}
                            </>
                        }                    
                    </div>
                ) : null}

           </div>

           {CARD_ACTIONS[action] && (
            isLoading ? <Spinner /> : 
            <div className="card-action-status-action-buttons">
                <div className="card-action-withdrawal-button" onClick={handleAction}>
                    {t("confirm")}
                </div>
            </div>)}
        </div>
    )

}

export default CardActionModal